import * as React from 'react';

import { cn } from 'lib/utils';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
	({ className, ...props }, ref) => (
		// tabIndex={0}: bug real de accesibilidad encontrado con axe
		// (scrollable-region-focusable) en Screens/Movimientos B2B — este div es
		// la única razón por la que una tabla ancha no desborda la página (ver
		// Foundations/Responsive), pero sin poder recibir foco un usuario de
		// teclado no puede desplazarlo en horizontal cuando desborda: solo el
		// ratón/scroll táctil llegaban a las columnas fuera de la vista.
		<div className="relative w-full overflow-auto" tabIndex={0}>
			<table
				ref={ref}
				className={cn('w-full caption-bottom text-sm border-spacing-0', className)}
				{...props}
			/>
		</div>
	)
);
Table.displayName = 'Table';

// border-border, ya no border-gray-100 dark:border-white/10 (11 sep 2026,
// aplicando Foundations/DarkMode): --border era "demasiado pesado" como
// hairline de tabla, así que se esquivaba con un literal Tailwind fijo en
// vez de arreglar el token — el sistema se saltaba su propio borde. Ahora
// --border tiene un solo rol (hairline, defecto 5) y ya es lo bastante
// sutil para usarse aquí directamente, en los dos temas, sin parche.
const TableHeader = React.forwardRef<
	HTMLTableSectionElement,
	React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
	<thead ref={ref} className={cn('[&_tr]:border-b [&_tr]:border-border', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
	HTMLTableSectionElement,
	React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
	<tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
	HTMLTableSectionElement,
	React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
	<tfoot
		ref={ref}
		className={cn(
			'border-t border-border bg-muted/50 font-medium last:[&>tr]:border-b-0',
			className
		)}
		{...props}
	/>
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
	({ className, ...props }, ref) => (
		<tr
			ref={ref}
			className={cn(
				// Línea divisoria "gris clarita" a propósito, no el token --border
				// (bg-primary/10... aparte): --border se corrigió a un gris mucho más
				// oscuro para pasar el contraste no-textual de
				// SC 1.4.11 en los límites de inputs/cards — esa
				// exigencia es de "límite de componente interactivo", no de un
				// separador de filas puramente organizativo, así que la línea de la
				// tabla no hereda ese token: un gris explícito y sutil, igual en los
				// dos temas.
				'border-b border-border transition-colors data-[state=selected]:bg-muted hover:bg-muted/50',
				className
			)}
			{...props}
		/>
	)
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
	HTMLTableCellElement,
	React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
	<th
		ref={ref}
		className={cn(
			'h-12 px-4 text-left align-middle font-medium text-muted-foreground has-[[role=checkbox]]:pr-0',
			className
		)}
		{...props}
	/>
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
	HTMLTableCellElement,
	React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
	<td
		ref={ref}
		className={cn('p-4 align-middle has-[[role=checkbox]]:pr-0', className)}
		{...props}
	/>
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
	HTMLTableCaptionElement,
	React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
	<caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
